import React, { useRef, useState } from "react";
import { FaDownload, FaUpload } from "react-icons/fa";
import { Tooltip } from "@mui/material";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { DialogCustomAnimation } from "./dialog";

import PropTypes from "prop-types";

// 🔹 Helper to convert Excel serial date or string into MM/DD/YYYY
const formatExcelDate = (value) => {
  if (!value) return "";

  // If it's already a string, try to parse it
  if (typeof value === "string") {
    const parsed = new Date(value);
    if (!isNaN(parsed)) {
      return (
        (parsed.getMonth() + 1).toString().padStart(2, "0") +
        "/" +
        parsed.getDate().toString().padStart(2, "0") +
        "/" +
        parsed.getFullYear()
      );
    }
    return value; // return as is if invalid
  }

  // If it's a number → Excel serial date
  if (typeof value === "number") {
    const excelEpoch = new Date(1899, 11, 30); // Excel starts at 1900-01-01
    const parsed = new Date(excelEpoch.getTime() + value * 86400000);
    return (
      (parsed.getMonth() + 1).toString().padStart(2, "0") +
      "/" +
      parsed.getDate().toString().padStart(2, "0") +
      "/" +
      parsed.getFullYear()
    );
  }

  return "";
};

// 🔹 Helper to format date for backend (MM/dd/yyyy to yyyy-MM-dd)
const formatDateForBackend = (dateString) => {
  if (!dateString || dateString.trim() === "") return null;

  try {
    // Parse MM/dd/yyyy and convert to yyyy-MM-dd
    const [month, day, year] = dateString.split("/");
    if (month && day && year) {
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }
    return null;
  } catch (error) {
    console.error("Error formatting date:", error);
    return null;
  }
};

const CsvHandler = ({ onUploadComplete, onCsvUpload,templateName }) => {
  const [uploading, setUploading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  // 📥 Download Excel Template with dropdowns + date validation
  const handleDownloadExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Employee Template");

    // Headers
    const headers = [
      "First Name",
      "Middle Name",
      "Last Name",
      "Organizational Email",
      "Business Unit",
      "Role",
      "Job Title",
      "Manager",
      "Date of Joining",
    ];
    worksheet.addRow(headers);

    // Add some blank rows so dropdowns/date validation are visible
    for (let i = 0; i < 5; i++) {
      worksheet.addRow(["", "", "", "", "", "", "", "", ""]);
    }

    // Dropdown options
    const businessUnits = ["HR", "Finance", "Engineering", "Marketing"];
    const roles = ["Admin", "Manager", "Employee"];
    const jobTitles = ["Developer", "Analyst", "Designer"];
    const managers = [];

    // Apply dropdowns + date validation
    for (let rowNumber = 2; rowNumber <= 10; rowNumber++) {
      // worksheet.getCell(`E${rowNumber}`).dataValidation = {
      //   type: "list",
      //   allowBlank: true,
      //   formulae: [`"${businessUnits.join(",")}"`],
      // };
      worksheet.getCell(`F${rowNumber}`).dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: [`"${roles.join(",")}"`],
      };
      // worksheet.getCell(`G${rowNumber}`).dataValidation = {
      //   type: "list",
      //   allowBlank: true,
      //   formulae: [`"${jobTitles.join(",")}"`],
      // };
      worksheet.getCell(`H${rowNumber}`).dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: [`"${managers.join(",")}"`],
      };

      // Date of Joining column
      const dateCell = worksheet.getCell(`I${rowNumber}`);
      dateCell.dataValidation = {
        type: "date",
        operator: "between",
        formulae: [new Date(1900, 0, 1), new Date(2100, 11, 31)],
        showErrorMessage: true,
        errorTitle: "Invalid Date",
        error: "Please enter a valid date between 1900 and 2100",
      };
      dateCell.numFmt = "mm/dd/yyyy"; // Format cell as date
    }

    // Export file
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "employee_template.xlsx");
  };

   // 📥 Download Excel Template with dropdowns + date validation
const handleDepartmentDownloadExcel = async () => {
  const workbook = new ExcelJS.Workbook();

  const worksheet = workbook.addWorksheet(
    "Department & Job Title Template"
  );

  // ✅ Explicitly define ONLY 2 columns
  worksheet.columns = [
    { header: "Business Unit", key: "businessUnit", width: 25 },
    { header: "Job Title", key: "jobTitle", width: 30 },
  ];

  // (Optional) add 1–2 blank rows
  worksheet.addRow({ businessUnit: "", jobTitle: "" });
  worksheet.addRow({ businessUnit: "", jobTitle: "" });
  
worksheet.getColumn(3).hidden = true;
worksheet.getColumn(4).hidden = true;
worksheet.getColumn(5).hidden = true;

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer]),
    "department_job-title_template.xlsx"
  );
};



  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      console.log("File selected:", file.name);
    }
    // Reset the input to allow selecting the same file again
    event.target.value = "";
  };

  const handleDropZoneClick = (e) => {
    e.stopPropagation(); // Prevent event bubbling
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      setSelectedFile(file);
      console.log("File dropped:", file.name);
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      console.warn("No file selected for submission");
      return;
    }

    try {
      setUploading(true);
      await onCsvUpload(selectedFile);
      // Close dialog and reset state
      setIsDialogOpen(false);
      setSelectedFile(null);
      console.log("Upload completed successfully");
    } catch (error) {
      console.error("Upload failed:", error);
      // You might want to show an error message to the user here
    } finally {
      setUploading(false);
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setSelectedFile(null);
  };

  return (
    <div className="flex items-center gap-2">
      {/* Upload Excel */}
      <Tooltip title="Upload Excel File">
        <button
          type="button"
          onClick={() => setIsDialogOpen(true)}
          disabled={uploading}
          className="px-3 sm:px-4 md:px-6 py-2.5 sm:py-3 bg-white text-blue-600 flex items-center justify-center gap-1.5 sm:gap-2 rounded-xl font-semibold hover:bg-blue-50 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 whitespace-nowrap"
        >
          <FaUpload className="w-4 h-4" />
          {uploading ? "Uploading..." : "Import File"}
        </button>
      </Tooltip>

      <DialogCustomAnimation
        open={isDialogOpen}
        handleClose={handleDialogClose}
        size="sm"
        handleSave={handleSubmit}
        saveLabel="Submit"
        saveDisabled={!selectedFile || uploading}
        body={
          <div className="space-y-4">
            <div className="flex items-center justify-center">
              <button
                type="button"
                onClick={()=>templateName=="Department"?handleDepartmentDownloadExcel(): handleDownloadExcel()}
                disabled={uploading}
                className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 transition-colors text-sm font-medium"
              >
                <FaDownload className="w-4 h-4" />
                Download Template
              </button>
            </div>
            <p className="text-sm text-gray-700">
              Download a template with the required column format.
            </p>
            <button
              type="button"
              aria-label="Drop your CSV or Excel file here, or click to browse files"
              className="w-full rounded-md border-2 border-dashed border-gray-300 bg-gray-50 p-6 text-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:bg-gray-100 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              onClick={handleDropZoneClick}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={handleFileDrop}
              disabled={uploading}
            >
              <div className="mx-auto mb-3 w-10 h-10 flex items-center justify-center rounded-md bg-white shadow">
                <span className="text-gray-500 text-xl">📄</span>
              </div>
              <p className="text-sm text-gray-800 font-medium">
                Drop your CSV or Excel file here, or click to browse
              </p>
              <p className="text-xs text-gray-500 mt-1">
                File should contain header information with required columns
              </p>
              {selectedFile && (
                <p className="mt-3 text-xs text-gray-700">
                  Selected:{" "}
                  <span className="font-medium">{selectedFile.name}</span>
                  <br />
                  <span className="text-green-600">Ready to upload!</span>
                </p>
              )}
            </button>

            {/* Hidden file input - MOVED INSIDE THE DIALOG BODY */}
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileSelect}
              disabled={uploading}
            />
          </div>
        }
      />
    </div>
  );
};

CsvHandler.propTypes = {
  onUploadComplete: PropTypes.func,
  onCsvUpload: PropTypes.func,
};

CsvHandler.defaultProps = {
  onUploadComplete: () => {},
  onCsvUpload: () => {},
};

export default CsvHandler;
